import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Header, Footer } from "@/components/Layout";
import { BarChart3, Stethoscope, Users, ShieldCheck, FileSignature, ScrollText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";

const BASE_NAV = [
  { to: "/admin", end: true, icon: BarChart3, label: "Analíticas", testid: "admin-nav-analiticas" },
  { to: "/admin/consultas", icon: Stethoscope, label: "Consultas", testid: "admin-nav-consultas" },
  { to: "/admin/pacientes", icon: Users, label: "Personas usuarias", testid: "admin-nav-pacientes" },
  { to: "/admin/medicos", icon: Stethoscope, label: "Médicos", testid: "admin-nav-medicos" },
  { to: "/admin/firmas", icon: FileSignature, label: "Firmas del Acuerdo", testid: "admin-nav-firmas" },
];
const SUPERADMIN_ONLY_NAV = [
  { to: "/admin/auditoria", icon: ScrollText, label: "Auditoría", testid: "admin-nav-auditoria" },
];

export default function AdminLayout() {
  const { user } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/", { replace: true });
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-8">
        <section className="mb-6" data-testid="admin-header">
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-[11px] font-medium text-[#0F2942]">
            <ShieldCheck size={12} className="text-[#2563EB]" />
            Panel de administración · Siempre Salud
            {user?.is_superadmin && (
              <span
                className="ml-1 inline-flex items-center gap-1 rounded-full bg-[#2563EB]/10 text-[#2563EB] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest"
                data-testid="admin-superadmin-chip"
                title="Cuenta con permisos ampliados: acceso a archivos y respuestas IA con auditoría reforzada."
              >
                <ShieldCheck size={10} /> Superadmin
              </span>
            )}
          </div>
          <h1 className="mt-2 font-display text-3xl font-semibold text-[#0F2942] tracking-tight">
            Consola interna
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Acceso restringido a cuentas con dominio interno (@transsalud.com, @massclinik.com, @siempresalud.mx).
            Todas las acciones se registran en el audit log.
          </p>
        </section>

        <nav
          className="border-b border-border flex gap-1 sm:gap-3 overflow-x-auto"
          data-testid="admin-tabs"
        >
          {[...BASE_NAV, ...(user?.is_superadmin ? SUPERADMIN_ONLY_NAV : [])].map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 px-3 py-3 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
                  isActive
                    ? "border-[#2563EB] text-[#0F2942]"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`
              }
            >
              <n.icon size={14} /> {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="mt-6">
          <Outlet />
        </div>
      </main>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
}
